import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { authApi, fetchCsrfCookie, subscriptionPlanApi, SubscriptionPlan, RegisterData, RegisterConsent } from '../services/api';
import { getThrottleMessage } from '../utils/throttleMessage';
import { Check } from 'iconoir-react';
import { OAuthButtons } from '../components/OAuthButtons';
import { resolvePlanBillingPeriod } from '../utils/planPricing';
import '../styles/register.css';

type AccountType = 'individual' | 'company';
type BillingPeriod = 'monthly' | 'yearly';

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Cas particulier : l'adresse e-mail est déjà associée à un compte Ventilio.
  // On affiche alors une UX dédiée (proposer la connexion) plutôt qu'une erreur brute.
  const [emailAlreadyUsed, setEmailAlreadyUsed] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [legalConsents, setLegalConsents] = useState<{ cgu: boolean; cgv: boolean; privacy: boolean }>({
    cgu: false,
    cgv: false,
    privacy: false,
  });

  // UX04 — si on arrive avec ?oauth_token=...&oauth_provider=..., on hydrate le form
  // depuis les infos retournées par /api/oauth/finalize et on désactive le password.
  const oauthToken = searchParams.get('oauth_token');
  const oauthProvider = searchParams.get('oauth_provider');
  const isOAuthFlow = Boolean(oauthToken && oauthProvider);

  const [formData, setFormData] = useState<RegisterData>({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    account_type: 'individual',
    plan_id: 0,
    billing_period: 'monthly',
  });

  useEffect(() => {
    loadPlans();
    if (isOAuthFlow && oauthToken) {
      loadOAuthData(oauthToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOAuthData = async (token: string) => {
    try {
      const response = await api.post('/oauth/finalize', { oauth_token: token });
      setFormData(prev => ({
        ...prev,
        name: response.data.name ?? prev.name,
        email: response.data.email ?? prev.email,
      }));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lien OAuth expiré, recommencez la connexion.');
    }
  };

  const loadPlans = async () => {
    try {
      const response = await subscriptionPlanApi.getAll();
      setPlans(response.data.plans);

      const preselectedPlanName = searchParams.get('plan');
      if (preselectedPlanName) {
        const preselectedPlan = response.data.plans.find((p) => p.name === preselectedPlanName);
        if (preselectedPlan) {
          setFormData(prev => ({
            ...prev,
            plan_id: preselectedPlan.id,
            billing_period: resolvePlanBillingPeriod(preselectedPlan),
          }));
        }
      }
    } catch (err) {
      console.error('Error loading plans:', err);
      setError('Impossible de charger les plans d\'abonnement');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Dès que l'utilisateur retouche son adresse, on lève l'erreur « email déjà utilisé ».
    if (name === 'email' && emailAlreadyUsed) {
      setEmailAlreadyUsed(false);
      setError(null);
    }
  };

  // Revient à l'étape « informations personnelles » pour corriger l'adresse e-mail,
  // en conservant toutes les autres saisies (elles vivent dans formData).
  const handleEditEmail = () => {
    setEmailAlreadyUsed(false);
    setError(null);
    setCurrentStep(2);
  };

  // Redirige vers la connexion en préremplissant uniquement l'adresse e-mail
  // (jamais le mot de passe). Pas de connexion automatique.
  const handleGoToLogin = () => {
    const params = new URLSearchParams({ email: formData.email });
    navigate(`/login?${params.toString()}`);
  };

  const handleAccountTypeChange = (type: AccountType) => {
    setFormData(prev => ({ ...prev, account_type: type }));
  };

  const handlePlanSelect = (planId: number) => {
    setFormData(prev => ({ ...prev, plan_id: planId }));
  };

  const handleBillingPeriodChange = (period: BillingPeriod) => {
    setFormData(prev => ({ ...prev, billing_period: period }));
  };

  const validateStep = (step: number): boolean => {
    setError(null);

    switch (step) {
      case 1:
        return true; // Account type selection

      case 2:
        // UX04 — en flow OAuth, l'email/name viennent du provider et il n'y a pas de password.
        if (!formData.name || !formData.email) {
          setError('Veuillez remplir tous les champs obligatoires');
          return false;
        }
        if (!isOAuthFlow) {
          if (!formData.password) {
            setError('Veuillez remplir tous les champs obligatoires');
            return false;
          }
          if (formData.password !== formData.password_confirmation) {
            setError('Les mots de passe ne correspondent pas');
            return false;
          }
          if (formData.password.length < 8) {
            setError('Le mot de passe doit contenir au moins 8 caractères');
            return false;
          }
        }
        if (formData.account_type === 'company') {
          // UX01 : SIRET et numero TVA sont optionnels ; code pays retire du formulaire.
          if (!formData.company_name || !formData.billing_address ||
              !formData.billing_city || !formData.billing_postal_code ||
              !formData.billing_contact) {
            setError('Veuillez remplir tous les champs obligatoires pour une société');
            return false;
          }
          // Si le SIRET est fourni, il doit faire 14 chiffres ; sinon on l'accepte vide.
          if (formData.siret && formData.siret.length !== 14) {
            setError('Le SIRET doit contenir 14 chiffres');
            return false;
          }
        }
        return true;

      case 3:
        if (!formData.plan_id) {
          setError('Veuillez sélectionner un plan');
          return false;
        }

        // Validation du formulaire publicitaire pour le plan gratuit
        // Les champs pub ne sont obligatoires QUE si l'utilisateur a consenti à la personnalisation
        const selectedPlan = plans.find(p => p.id === formData.plan_id);
        if (selectedPlan && selectedPlan.has_ads && formData.advertising_consent) {
          if (!formData.advertising_interests || formData.advertising_interests.length < 3) {
            setError('Veuillez sélectionner 3 centres d\'intérêt publicitaires');
            return false;
          }

          if (!formData.pcs_level_1 || !formData.pcs_level_2) {
            setError('Veuillez sélectionner votre catégorie socio-professionnelle');
            return false;
          }

          if (!formData.advertising_age) {
            setError('Veuillez sélectionner votre tranche d\'âge');
            return false;
          }

          if (!formData.advertising_gender) {
            setError('Veuillez sélectionner votre sexe');
            return false;
          }
        }
        return true;

      case 4:
        return true; // RGPD consents

      case 5:
        return true; // Payment (if needed)

      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
    setError(null);
    setEmailAlreadyUsed(false);
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Le profil publicitaire est désormais complété après l'inscription.
      const consents: RegisterConsent[] = [
        { type: 'cgu', accepted: legalConsents.cgu },
        { type: 'cgv', accepted: legalConsents.cgv },
        { type: 'privacy_policy', accepted: legalConsents.privacy },
      ];

      const dataWithConsents: RegisterData = {
        ...formData,
        consents,
        ...(oauthToken ? { oauth_token: oauthToken } : {}),
      };
      await fetchCsrfCookie();
      const response = await authApi.register(dataWithConsents);

      navigate('/verify-email', {
        state: {
          email: formData.email,
          message: response.data.message
        }
      });
    } catch (err: any) {
      console.error('Registration error:', err);
      const res = err.response;
      const emailErrors: string[] = res?.data?.errors?.email ?? [];
      const isDuplicateEmail =
        res?.status === 422 &&
        emailErrors.some((m) => /existe d[ée]j[àa]|already been taken/i.test(m));

      if (res?.status === 429) {
        // Trop de tentatives : message français, avec délai si le backend le fournit.
        setError(getThrottleMessage(err));
      } else if (isDuplicateEmail) {
        // Adresse déjà rattachée à un compte : UX dédiée (voir bloc plus bas).
        setEmailAlreadyUsed(true);
        setError('Un compte existe déjà avec cette adresse e-mail.');
      } else if (res && res.status >= 500) {
        // La cause réelle est journalisée côté backend ; on n'expose jamais
        // le message technique (stack trace, erreur SQL, "Server Error"...).
        setError('Une erreur est survenue lors de la création de votre compte. Veuillez réessayer.');
      } else {
        // 422 (validation), etc. : message métier du backend.
        setError(res?.data?.message || 'Une erreur est survenue lors de l\'inscription');
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = plans.find(p => p.id === formData.plan_id);

  return (
    <div className="register-page">
      <div className="register-container">
        <div className="register-header">
          <img src="/assets/ventilio.png" alt="Ventilio" className="register-logo" />
          <h1>Créer un compte Ventilio</h1>
          <div className="step-indicator">
            {[1, 2, 3, 4].map((step) => (
              <div
                key={step}
                className={`step ${currentStep === step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}
              >
                {currentStep > step ? <Check /> : step}
              </div>
            ))}
          </div>
        </div>

        {emailAlreadyUsed ? (
          <div className="error-message email-exists-notice" role="alert">
            <p>Un compte existe déjà avec cette adresse e-mail.</p>
            <div className="email-exists-actions">
              <button type="button" className="btn btn-primary" onClick={handleGoToLogin}>
                Se connecter
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleEditEmail}>
                Modifier l'adresse
              </button>
            </div>
          </div>
        ) : (
          error && (
            <div className="error-message">
              {error}
            </div>
          )
        )}

        <div className="register-form">
          {currentStep === 1 && (
            <StepAccountType
              accountType={formData.account_type}
              onSelect={handleAccountTypeChange}
              onNext={nextStep}
              showOAuth={!isOAuthFlow}
            />
          )}

          {currentStep === 2 && (
            <StepUserInfo
              formData={formData}
              onChange={handleInputChange}
              onNext={nextStep}
              onPrev={prevStep}
              isOAuthFlow={isOAuthFlow}
              oauthProvider={oauthProvider}
            />
          )}

          {currentStep === 3 && (
            <StepPlanSelection 
              plans={plans}
              selectedPlanId={formData.plan_id}
              billingPeriod={formData.billing_period}
              onSelectPlan={handlePlanSelect}
              onSelectBillingPeriod={handleBillingPeriodChange}
              onUpdateFormData={(updates) => setFormData(prev => ({ ...prev, ...updates }))}
              onNext={nextStep}
              onPrev={prevStep}
            />
          )}

          {currentStep === 4 && (
            <StepConsents
              selectedPlan={selectedPlan}
              onNext={handleSubmit}
              onPrev={prevStep}
              loading={loading}
              onConsentsChange={setLegalConsents}
            />
          )}
        </div>
      </div>
    </div>
  );
};

interface StepAccountTypeProps {
  accountType: AccountType;
  onSelect: (type: AccountType) => void;
  onNext: () => void;
  showOAuth?: boolean;
}

const StepAccountType: React.FC<StepAccountTypeProps> = ({ accountType, onSelect, onNext, showOAuth }) => {
  return (
    <div className="step-content">
      <h2>Type de compte</h2>
      <p>Sélectionnez le type de compte qui vous correspond</p>

      <div className="account-type-options">
        <div
          className={`account-type-card ${accountType === 'individual' ? 'selected' : ''}`}
          onClick={() => onSelect('individual')}
        >
          <h3>Particulier</h3>
          <p>Pour un usage personnel</p>
        </div>

        <div
          className={`account-type-card ${accountType === 'company' ? 'selected' : ''}`}
          onClick={() => onSelect('company')}
        >
          <h3>Société</h3>
          <p>Pour un usage professionnel avec facturation</p>
        </div>
      </div>

      <div className="form-actions">
        <button className="btn btn-primary" onClick={onNext}>
          Suivant
        </button>
      </div>

      {showOAuth && <OAuthButtons mode="register" />}
    </div>
  );
};

interface StepUserInfoProps {
  formData: RegisterData;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onNext: () => void;
  onPrev: () => void;
  isOAuthFlow?: boolean;
  oauthProvider?: string | null;
}

const StepUserInfo: React.FC<StepUserInfoProps> = ({ formData, onChange, onNext, onPrev, isOAuthFlow, oauthProvider }) => {
  return (
    <div className="step-content">
      <h2>Informations {formData.account_type === 'company' ? 'de la société' : 'personnelles'}</h2>
      <h3>{formData.account_type === 'company' ? 'Représentant' : ''}</h3>

      {isOAuthFlow && (
        <div className="oauth-notice">
          Connexion via <strong>{oauthProvider === 'microsoft' ? 'Microsoft' : 'Google'}</strong> :
          votre nom et email ont été pré-remplis, et aucun mot de passe n'est nécessaire.
        </div>
      )}

      <div className="form-group">
        <label>Nom complet *</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={onChange}
          placeholder="Jean Dupont"
          required
        />
      </div>

      <div className="form-group">
        <label>Email *</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={onChange}
          placeholder="jean.dupont@example.com"
          required
          disabled={isOAuthFlow}
        />
      </div>

      {!isOAuthFlow && (
        <>
          <div className="form-group">
            <label>Mot de passe *</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={onChange}
              placeholder="Minimum 8 caractères"
              required
            />
          </div>

          <div className="form-group">
            <label>Confirmer le mot de passe *</label>
            <input
              type="password"
              name="password_confirmation"
              value={formData.password_confirmation}
              onChange={onChange}
              placeholder="Confirmez votre mot de passe"
              required
            />
          </div>
        </>
      )}

      {formData.account_type === 'company' && (
        <>
          <h3>Informations de facturation</h3>
          
          <div className="form-group">
            <label>Nom de la société *</label>
            <input
              type="text"
              name="company_name"
              value={formData.company_name || ''}
              onChange={onChange}
              required
            />
          </div>

          <div className="form-group">
            <label>SIRET (14 chiffres, optionnel)</label>
            <input
              type="text"
              name="siret"
              value={formData.siret || ''}
              onChange={onChange}
              placeholder="12345678901234"
              maxLength={14}
            />
          </div>

          <div className="form-group">
            <label>Adresse de facturation *</label>
            <input
              type="text"
              name="billing_address"
              value={formData.billing_address || ''}
              onChange={onChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Ville *</label>
              <input
                type="text"
                name="billing_city"
                value={formData.billing_city || ''}
                onChange={onChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Code postal *</label>
              <input
                type="text"
                name="billing_postal_code"
                value={formData.billing_postal_code || ''}
                onChange={onChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Contact facturation *</label>
            <input
              type="text"
              name="billing_contact"
              value={formData.billing_contact || ''}
              onChange={onChange}
              placeholder="Nom du contact"
              required
            />
          </div>

          <div className="form-group">
            <label>Numéro de TVA (optionnel)</label>
            <input
              type="text"
              name="vat_number"
              value={formData.vat_number || ''}
              onChange={onChange}
              placeholder="FR12345678901"
            />
          </div>
        </>
      )}

      <div className="form-actions">
        <button className="btn btn-secondary" onClick={onPrev}>
          Précédent
        </button>
        <button className="btn btn-primary" onClick={onNext}>
          Suivant
        </button>
      </div>
    </div>
  );
};

interface StepPlanSelectionProps {
  plans: SubscriptionPlan[];
  selectedPlanId: number;
  billingPeriod: BillingPeriod;
  onSelectPlan: (planId: number) => void;
  onSelectBillingPeriod: (period: BillingPeriod) => void;
  onUpdateFormData: (updates: Partial<RegisterData>) => void;
  onNext: () => void;
  onPrev: () => void;
}

const StepPlanSelection: React.FC<StepPlanSelectionProps> = ({
  plans,
  selectedPlanId,
  billingPeriod,
  onSelectPlan,
  onSelectBillingPeriod,
  onUpdateFormData,
  onNext,
  onPrev
}) => {
  const [planBillingPeriods, setPlanBillingPeriods] = useState<{ [key: number]: BillingPeriod }>({});

  const getPlanBillingPeriod = (planId: number): BillingPeriod => {
    return planBillingPeriods[planId] || 'monthly';
  };

  const setPlanBillingPeriod = (planId: number, period: BillingPeriod) => {
    setPlanBillingPeriods(prev => ({ ...prev, [planId]: period }));
    // Plan à double tarif déjà sélectionné : garder formData.billing_period en phase.
    if (selectedPlanId === planId) {
      onUpdateFormData({ billing_period: period });
    }
  };

  // Périodicité effective d'un plan : imposée par le plan lui-même (tarif unique),
  // sauf ancien plan à double tarif où l'on respecte le toggle Mensuel/Annuel.
  const effectiveBillingPeriod = (plan: SubscriptionPlan): BillingPeriod =>
    resolvePlanBillingPeriod(plan, getPlanBillingPeriod(plan.id));

  // Logique de sélection unique : utilisée par le clic sur la carte, le clic sur
  // le bouton et le clavier (Entrée / Espace) — un seul chemin, aucun double effet.
  // La périodicité est toujours déduite du plan, jamais d'un état indépendant.
  const selectPlan = (plan: SubscriptionPlan) => {
    onSelectPlan(plan.id);
    onUpdateFormData({ billing_period: effectiveBillingPeriod(plan) });
  };

  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, plan: SubscriptionPlan) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      selectPlan(plan);
    }
  };

  return (
    <div className="step-content">
      <h2>Choisissez votre plan</h2>

      <div className="plan-education-text">
        <h3>Comprendre nos tarifs</h3>
        <p>
          Développer et maintenir une application comme Ventilio représente des coûts importants : 
          serveurs, stockage, bande passante, développement, maintenance, support client, et conformité RGPD.
        </p>
        <p>
          Pour vous offrir une option 100% gratuite, nous proposons un modèle financé par la publicité. 
          Les revenus publicitaires nous permettent de couvrir ces coûts et de continuer à améliorer Ventilio pour tous.
        </p>
        <p>
          Si vous préférez une expérience sans publicité, nos plans Premium vous offrent 
          une interface épurée et des fonctionnalités avancées.
        </p>
        <div className="plan-reversibility-notice">
          Votre choix est réversible à tout moment et vous pourrez passer d'un plan à l'autre quand vous le souhaitez
        </div>
      </div>

      <div className="plans-grid">
        {plans.map(plan => {
          const hasBothPrices = plan.price_monthly > 0 && plan.price_yearly > 0;
          const currentPeriod = effectiveBillingPeriod(plan);
          const currentPrice = currentPeriod === 'monthly' ? plan.price_monthly : plan.price_yearly;
          const displayPrice = typeof currentPrice === 'number' ? currentPrice : parseFloat(currentPrice || '0');
          const priceLabel = currentPeriod === 'monthly' ? '/mois' : '/an';

          return (
            <div
              key={plan.id}
              className={`plan-card ${selectedPlanId === plan.id ? 'selected' : ''} ${plan.has_ads ? 'free' : 'premium'}`}
              role="button"
              tabIndex={0}
              aria-pressed={selectedPlanId === plan.id}
              aria-label={`Choisir le plan ${plan.display_name}`}
              onClick={() => selectPlan(plan)}
              onKeyDown={(e) => handleCardKeyDown(e, plan)}
            >
              {plan.has_ads && <div className="plan-badge">Gratuit</div>}
              {!plan.has_ads && <div className="plan-badge premium">Premium</div>}

              <h3>{plan.display_name}</h3>
              <p className="plan-description">{plan.description}</p>

              {hasBothPrices && (
                <div className="billing-period-toggle">
                  <button
                    type="button"
                    className={`toggle-btn ${currentPeriod === 'monthly' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPlanBillingPeriod(plan.id, 'monthly');
                    }}
                  >
                    Mensuel
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn ${currentPeriod === 'yearly' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPlanBillingPeriod(plan.id, 'yearly');
                    }}
                  >
                    Annuel
                  </button>
                </div>
              )}

              <div className="plan-price">
                <span className="price">{displayPrice.toFixed(2)}€</span>
                <span className="period">{priceLabel}</span>
              </div>

              {hasBothPrices && currentPeriod === 'yearly' && (
                <div className="plan-savings">
                  Économisez {((plan.price_monthly * 12) - plan.price_yearly).toFixed(2)}€/an
                </div>
              )}

              <ul className="plan-features">
                {plan.features.map((feature, index) => (
                  <li key={index}>
                    <Check width={16} height={16} /> {feature}
                  </li>
                ))}
              </ul>

              <button
                type="button"
                className="btn btn-select-plan"
                onClick={(e) => {
                  // stopPropagation : évite un second déclenchement via le onClick de la carte.
                  e.stopPropagation();
                  selectPlan(plan);
                }}
              >
                {selectedPlanId === plan.id ? 'Plan sélectionné' : 'Choisir ce plan'}
              </button>
            </div>
          );
        })}
      </div>

      <div className="form-actions">
        <button className="btn btn-secondary" onClick={onPrev}>
          Précédent
        </button>
        <button 
          className="btn btn-primary" 
          onClick={onNext}
          disabled={!selectedPlanId}
        >
          Suivant
        </button>
      </div>
    </div>
  );
};

interface StepConsentsProps {
  selectedPlan?: SubscriptionPlan;
  onNext: () => void;
  onPrev: () => void;
  loading: boolean;
  onConsentsChange: (consents: { cgu: boolean; cgv: boolean; privacy: boolean }) => void;
}

const StepConsents: React.FC<StepConsentsProps> = ({ selectedPlan, onNext, onPrev, loading, onConsentsChange }) => {
  const [consents, setConsents] = useState({
    cgu: false,
    cgv: false,
    privacy: false,
  });

  const handleConsentChange = (key: keyof typeof consents) => {
    const updated = { ...consents, [key]: !consents[key] };
    setConsents(updated);
    onConsentsChange(updated);
  };

  const allConsentsGiven = consents.cgu && consents.cgv && consents.privacy;

  return (
    <div className="step-content">
      <h2>Consentements RGPD</h2>
      <p>Veuillez accepter les conditions suivantes pour continuer</p>

      <div className="consents-list">
        <label className="consent-item">
          <input
            type="checkbox"
            checked={consents.cgu}
            onChange={() => handleConsentChange('cgu')}
          />
          <span>
            J'accepte les <a href="/cgu" target="_blank" rel="noreferrer">Conditions Générales d'Utilisation</a> *
          </span>
        </label>

        <label className="consent-item">
          <input
            type="checkbox"
            checked={consents.cgv}
            onChange={() => handleConsentChange('cgv')}
          />
          <span>
            J'accepte les <a href="/cgv" target="_blank" rel="noreferrer">Conditions Générales de Vente</a> *
          </span>
        </label>

        <label className="consent-item">
          <input
            type="checkbox"
            checked={consents.privacy}
            onChange={() => handleConsentChange('privacy')}
          />
          <span>
            J'accepte la <a href="/privacy" target="_blank" rel="noreferrer">Politique de Confidentialité</a> *
          </span>
        </label>

        {selectedPlan?.has_ads && (
          <p className="consent-info">
            Le plan gratuit affiche des publicités. Vous pourrez choisir de personnaliser votre
            expérience publicitaire depuis votre espace Ventilio, une fois connecté.
          </p>
        )}
      </div>

      <div className="form-actions">
        <button className="btn btn-secondary" onClick={onPrev}>
          Précédent
        </button>
        <button 
          className="btn btn-primary" 
          onClick={onNext}
          disabled={!allConsentsGiven || loading}
        >
          {loading ? 'Inscription...' : 'Créer mon compte'}
        </button>
      </div>
    </div>
  );
};
